import { StrictMode } from 'react'
import type { ErrorInfo } from 'react'
import ReactDOM from 'react-dom/client'
import './studio.css'
// App is still .jsx; tsconfig's allowJs lets us import it without losing
// type safety on the typed contexts/hooks beneath it.
import App from './App.jsx'
import { ErrorBoundary } from './ErrorBoundary'
import { AuthProvider } from './contexts/AuthContext'
import { IntegrationHealthProvider } from './contexts/IntegrationHealthContext'
import { StudioStateProvider } from './contexts/StudioStateContext'
import { TruthProvider } from './contexts/TruthContext'
import { AuthorityProvider } from './contexts/AuthorityContext'
import { ProofProvider } from './contexts/ProofContext'
import { PillarsProvider } from './contexts/PillarsContext'
import { UndoProvider } from './contexts/UndoContext'
import { OperatorProfileProvider } from './contexts/OperatorProfileContext.jsx'

function reportBoundaryError(
  error: Error,
  errorInfo: ErrorInfo,
  errorId: string
): void {
  if (typeof window === 'undefined') return
  try {
    fetch('/api/client-errors', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        errorId,
        message: error?.message || 'Unknown error',
        stack: error?.stack || null,
        componentStack: errorInfo?.componentStack || null,
        url: window.location?.href || null,
        userAgent: window.navigator?.userAgent || null,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {})
  } catch {
    // Ignore — boundary must never re-throw.
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Studio bootstrap failed: #root element not found')
}

ReactDOM.createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary onError={reportBoundaryError}>
      <AuthProvider>
        <IntegrationHealthProvider autoFetch={false}>
          <StudioStateProvider>
            <TruthProvider>
              <AuthorityProvider>
                <ProofProvider>
                  <PillarsProvider>
                    <UndoProvider>
                      <OperatorProfileProvider>
                        <App />
                      </OperatorProfileProvider>
                    </UndoProvider>
                  </PillarsProvider>
                </ProofProvider>
              </AuthorityProvider>
            </TruthProvider>
          </StudioStateProvider>
        </IntegrationHealthProvider>
      </AuthProvider>
    </ErrorBoundary>
  </StrictMode>
)
